/**
 * Finding cards (DESIGN §3): severity edge, title, one-line detail, the why, then the quick actions from core
 * (FRAMEWORK T2: every Finding carries an action). Call/Text are real tel:/sms: links; Remind morphs to "Sent ✓";
 * "Where's the …" reveals where it's kept.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { ASK_A_MANAGER_LINE, YES_MARK_HANDLED, nextStepFor, placeState, stillOpenLine, findingNagItem, type Employee, type Finding, type QuickAction } from "@logwave/core";
import { findingDetail, findingIcon, findingTitle, guideFor, guidePath, routeFor, whyFor } from "../lib/meaning.ts";
import { useApi, useSession } from "../session.tsx";
import { Icon, type IconName } from "./Icons.tsx";
import { AnchorButton, Button, LinkButton } from "./ui.tsx";
import { checkActionLabel } from "../lib/labels.ts";
import { logReminder } from "../lib/remindersSent.ts";
import { lazyInline } from "../lib/lazy.ts";
/** Easy fixes under a finding load after the card is drawn (they bring the surface guides). */
const EasyFixes = lazyInline(() => import("./Ask.tsx"), "EasyFixes");
import { NagButton } from "./Nag.tsx";

/** Vague route labels ("Open", "Log it now") become the exact thing that happens ("Test sanitizer", "Open the report"). */
function actionLabel(a: QuickAction, f: Finding): string {
  if (a.kind === "do" && a.target?.type === "check" && f.kind === "missed_check") {
    const tpl = f.ref?.split("@")[0] ?? "";
    const verb = checkActionLabel(templateOfRule(tpl), f.title.replace(/^Still needed: /, ""));
    if (verb) return verb;
  }
  if (a.kind === "open" && /^open$/i.test(a.label)) return a.target?.type === "incident" ? "Open the report" : a.target?.type === "review" ? "Review the scan" : "Open the details";
  return a.label;
}
const templateOfRule = (ruleKey: string) => (/sanitizer/.test(ruleKey) ? "sanitizer" : /cold/.test(ruleKey) ? "cold_units" : /calib|thermo/.test(ruleKey) ? "calibration" : /restroom/.test(ruleKey) ? "restroom_check" : "");

const ACTION_ICON: Record<QuickAction["kind"], IconName> = { call: "phone", text: "text", remind: "bell", do: "do", fix: "wrench", open: "chevron", locate: "pin", reorder: "cart" };

export function QuickActions({ f, onShift }: { f: Finding; onShift?: Employee[] }) {
  const api = useApi();
  const s = useSession();
  const [sent, setSent] = useState<Record<number, string>>({});
  const [busy, setBusy] = useState<number>();
  const [open, setOpen] = useState<number>();

  async function remind(i: number, a: QuickAction) {
    setBusy(i);
    try {
      const message = a.detail ?? "Please check the CleanCleaver app.";
      if (a.target?.type === "employee") {
        const r = await api.remind(s.org.id, a.target.id, message);
        logReminder({ to: r.sentTo, message, at: new Date().toISOString() });
        setSent((m) => ({ ...m, [i]: `Sent to ${r.sentTo.split(" ")[0]} ✓` }));
      } else {
        // "Remind whoever's on shift": everyone on shift except me.
        const to = (onShift ?? []).filter((e) => e.id !== s.employeeId);
        if (!to.length) { setSent((m) => ({ ...m, [i]: "No one else on shift" })); return; }
        await Promise.all(to.map((e) => api.remind(s.org.id, e.id, message)));
        for (const e of to) logReminder({ to: e.name, message, at: new Date().toISOString() });
        setSent((m) => ({ ...m, [i]: `Sent to ${to.length === 1 ? to[0]!.name.split(" ")[0] : `${to.length} people`} ✓` }));
      }
    } catch (e) {
      setSent((m) => ({ ...m, [i]: e instanceof Error ? e.message : "Couldn't send" }));
    } finally { setBusy(undefined); }
  }

  // A finding about me: the action is mine to do, not a call to myself.
  const actions = f.ref && f.ref === s.employeeId && (f.kind === "not_checked_in" || f.kind === "not_cleared" || (f.kind === "credential" && !f.requirementIds.includes("cfsm")))
    ? [{ kind: "do" as const, label: f.kind === "not_checked_in" ? "Start my check-in" : f.kind === "credential" ? "Add my permit" : "Finish my lessons", target: { type: "employee" as const, id: f.ref } }]
    : f.actions;
  const nag = findingNagItem(f);
  if (!actions.length && !nag) return null;
  const detail = open !== undefined ? actions[open] : undefined;
  return (
    <>
      <div className="qa-row">
        {actions.map((a, i) => {
          // One primary per card: the first action (core orders them by what fixes it fastest).
          const variant = i === 0 ? "primary" : "secondary";
          const icon = ACTION_ICON[a.kind];
          if ((a.kind === "call" || a.kind === "text" || a.kind === "reorder") && a.href) return <AnchorButton key={i} small variant={variant} icon={icon} href={a.href}>{a.label}</AnchorButton>;
          if (a.kind === "remind") {
            const done = sent[i];
            return <Button key={i} small variant={variant} className={done ? "btn-done qa-morph" : undefined} icon={done ? "check" : icon} busy={busy === i} disabled={!!done} aria-live="polite" onClick={() => void remind(i, a)}>{done ?? a.label}</Button>;
          }
          if (a.kind === "locate") return <Button key={i} small variant={variant} icon={icon} aria-expanded={open === i} onClick={() => setOpen(open === i ? undefined : i)}>{a.label}</Button>;
          const to = a.kind === "do" && a.target?.type === "employee" ? (f.kind === "not_checked_in" ? "/check-in" : f.kind === "credential" ? "/learn#permit" : "/learn") : routeFor(a, f, s.employeeId);
          if (to) return <LinkButton key={i} small variant={variant} icon={icon} to={to}>{actionLabel(a, f)}</LinkButton>;
          return null;
        })}
        {/* Nag the person who can do it now (B19): overdue checks, problems waiting on a fix, open reports. */}
        {nag && <NagButton item={nag} />}
        {/* A supply finding: the whole order list, grouped by supplier (B18). */}
        {actions.some((a) => a.target?.type === "supply") && <LinkButton small variant="secondary" icon="cart" to="/order">Open the order list</LinkButton>}
      </div>
      {detail?.kind === "locate" && <p className="qa-detail" role="status"><Icon name="pin" size={18} /><span><strong>{detail.label.replace(/^Where's the /, "").replace(/\?$/, "").replace(/^./, (c) => c.toUpperCase())}:</strong> {detail.detail}</span></p>}
    </>
  );
}

export function FindingCard({ f, onShift }: { f: Finding; onShift?: Employee[] }) {
  return <li className={`finding finding-${f.severity}`}><FindingBody f={f} onShift={onShift} /></li>;
}

/** Title, detail, why and the quick actions of one finding (used in the Today list and the For You feed). */
export function FindingBody({ f, onShift }: { f: Finding; onShift?: Employee[] }) {
  const s = useSession();
  const why = f.kind === "missed_check" || f.kind === "failed_check" ? whyFor(f.requirementIds, placeState(s.location, s.org)) : undefined;
  const guide = guideFor(f.requirementIds, placeState(s.location, s.org));
  return (
    <>
      <div className="finding-title"><Icon name={findingIcon(f)} size={20} /><span>{f.ref && f.ref === s.employeeId ? findingTitle(f).replace(/^[^:]+: check-in still needed$/, "Your check-in is still needed").replace(/^[^:]+: lessons and sign-offs still needed$/, "Your lessons and sign-offs are still needed").replace(/^[^:]+: /, "You: ") : findingTitle(f)}</span></div>
      {f.detail && <p className="finding-detail">{f.ref && f.ref === s.employeeId ? findingDetail(f, s.location.timezone).replace(/\btheir phone\b/, "your phone") : findingDetail(f, s.location.timezone)}</p>}
      {why && <p className="finding-why">Why: {why}{guide && <> <Link to={guidePath(guide)}>Read the guide</Link></>}</p>}
      {f.kind === "incident" && f.incidentType && <OpenReportNext f={f} />}
      <QuickActions f={f} onShift={onShift} />
      {(f.kind === "failed_check" || f.kind === "missed_check") && <EasyFixes text={findingTitle(f)} situation={f.detail} />}
    </>
  );
}

/** An open report says what to do next, in plain words (core openreport.ts), and a manager closes it from here (owner, 2026-10-08: "do what now??"). */
function OpenReportNext({ f }: { f: Finding }) {
  const s = useSession();
  const next = nextStepFor(f.incidentType!);
  const still = f.reportedAt ? stillOpenLine({ status: "open", reportedAt: f.reportedAt, type: f.incidentType! }, new Date(), s.location.timezone) : undefined;
  const to = f.ref ? `/report#${f.ref}` : "/report";
  return (
    <div className="stack-sm" data-testid="open-report-next">
      <p className="finding-detail">{next.line}</p>
      {still && <p className="small"><strong>{still}</strong></p>}
      {next.canClose && s.can("manager") && <LinkButton small icon="check" to={to}>{still ? YES_MARK_HANDLED : "Mark it handled"}</LinkButton>}
      {next.canClose && !s.can("manager") && <p className="small">{ASK_A_MANAGER_LINE}.</p>}
    </div>
  );
}
